import { useLocale, useTranslations } from 'next-intl';

import { isNil } from 'lodash';

import { TranslationService } from '@/utils/services';

/**
 * Custom hook that adapts next-translate patterns to next-intl
 *
 * Provides backward compatibility for next-translate patterns while using next-intl under the hood
 * Handles namespace management and automatic key prefixing
 *
 * @param namespace - Optional namespace to prefix keys with
 * @returns Object with t function and current language
 */
const useTranslation = (namespace?: string): UseTranslationReturn => {
    const lang = useLocale();
    const tGlobal = useTranslations();

    const t = (key: string, values?: Record<string, any>, options?: { fallback?: string }) => {
        // Return key if it contains spaces (invalid key)
        if (isNil(key) || key.includes(' ')) return key;

        let translationKey = TranslationService.getTranslationKey(namespace, key);

        let translation = tGlobal(translationKey, values);

        if (translation === translationKey && options?.fallback) {
            return t(options.fallback, values);
        }

        return translation;
    };

    t.rich = (key: string, ...args) => {
        const translationKey = TranslationService.getTranslationKey(namespace, key);
        return tGlobal.rich(translationKey, ...args);
    };

    return { t, lang };
};

export type Translate =
    | ReturnType<typeof useTranslations>
    | ((key: string, values?: Record<string, any>, options?: { fallback?: string }) => string);

type UseTranslationReturn = {
    t: Translate & { rich: ReturnType<typeof useTranslations>['rich'] };
    lang: string;
};

export default useTranslation;
